import React, { forwardRef, useEffect, useState } from 'react';
import moment from 'moment';
import { styled, ButtonBase, ClickAwayListener, Popper } from '@mui/material';

import Avatar from 'shell/components/Avatar';
import AvatarGroup from 'shell/components/AvatarGroup';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Checkbox from 'shell/components/Checkbox';
import CompletionButton from 'shell/components/CompletionButton';
import Dropdown from 'shell/components/Dropdown';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import MenuItem from 'shell/components/MenuItem';
import MenuList from 'shell/components/MenuList';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';
import Tooltip from 'shell/components/Tooltip';
import { getTooltipComponent, getTooltipProps } from 'utils/helper';

const VARIANTS = ['default', 'simplified', 'milestone'];

const tooltipTexts = {
    due_date: { title: 'Due Date', description: 'When should this task be completed?' },
    collaborators: { title: 'Collaborators' },
    workload: {
        text: {
            title: 'Workload',
            description: 'An estimation of the time and effort it will take to complete this task',
        },
        select: {
            low: { title: 'Workload: Low', description: 'Low effort, time and/or resources to complete' },
            medium: { title: 'Workload: Medium', description: 'Moderate effort, time and/or resources to complete' },
            high: { title: 'Workload: High', description: 'Significant effort, time and/or resources to complete' },
        },
    },
    milestones: {
        title: 'Milestones',
        description: 'A milestone is a deliverable created by your instructor that requires a submission by your team',
    },
    tasks_associated: { true: { title: 'Hide tasks associated' }, false: { title: 'View tasks associated' } },
    status: {
        text: { title: 'Status', description: 'What stage is this task in?' },
        select: {
            toDo: {
                title: '"To Do" Column',
                description:
                    'Tasks that have been identified and prioritized for completion, but have not been started',
            },
            inProgress: {
                title: '"In Progress" Column',
                description: 'Tasks that are actively being worked on by team members',
            },
            underReview: {
                title: '"Under Review" Column',
                description: 'Tasks that are waiting for review or approval before being considered "done"',
            },
            completed: {
                title: '"Completed" Column',
                description: 'Tasks that have been reviewed and are now complete.',
            },
        },
    },
    subtasks: { true: { title: 'Close subtasks' }, false: { title: 'View subtasks' } },
};

const RootStack = styled(Stack)(({ isCompleted, isOverdue, theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(0, 1.5),
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.neutral[50],
        ...(isOverdue && {
            border: `1px solid ${theme.palette.ensightful.error[700]}`,
            background: theme.palette.ensightful.error[100],
            color: theme.palette.ensightful.error[700],
        }),
        ...(isCompleted && {
            border: `1px solid ${theme.palette.ensightful.success[700]}`,
            background: theme.palette.ensightful.shades[0],
            color: theme.palette.ensightful.success[700],
        }),
    };
});

const TagIcon = styled(Icon)(({ isCompleted, isOverdue, theme }) => {
    return {
        borderRadius: '50%',
        color: theme.palette.ensightful.shades[0],
        ...(isOverdue && {
            padding: '0.1875rem',
            fontSize: '1rem',
            background: theme.palette.ensightful.error[700],
        }),
        ...(isCompleted && {
            borderRadius: '50%',
            padding: '0.1875rem',
            fontSize: '1rem',
            background: theme.palette.ensightful.success[700],
            color: theme.palette.ensightful.shades[0],
        }),
    };
});

const CustomText = styled(Text)(() => {
    return {
        marginTop: '1px',
    };
});

const DueDateTag = ({ isCompleted, isOverdue, endOn, tooltip, variant }) => {
    if (variant === 'simplified') {
        return isCompleted ? (
            <RootStack
                alignItems={'center'}
                direction={'row'}
                isCompleted={isCompleted}
                isOverdue={isOverdue}
                spacing={1}
            >
                <TagIcon isCompleted={isCompleted} isOverdue={isOverdue} kind={'checkmark'} />
                <CustomText color={'inherit'} variant={'p3'}>
                    Completed
                </CustomText>
            </RootStack>
        ) : (
            <RootStack
                alignItems={'center'}
                direction={'row'}
                isCompleted={isCompleted}
                isOverdue={isOverdue}
                spacing={1}
            >
                <TagIcon isCompleted={isCompleted} isOverdue={isOverdue} kind={isOverdue ? 'error' : 'clock'} />
                <CustomText color={'inherit'} tooltip={tooltip} variant={'p3'}>
                    {isOverdue ? 'Overdue - ' : ''}
                    {moment(endOn).format('MMM D')}
                </CustomText>
            </RootStack>
        );
    } else {
        return (
            <RootStack alignItems={'center'} direction={'row'} isOverdue={!isCompleted && isOverdue} spacing={1}>
                <TagIcon isOverdue={!isCompleted && isOverdue} kind={!isCompleted && isOverdue ? 'error' : 'clock'} />
                <CustomText color={'inherit'} tooltip={tooltip} variant={'p3'}>
                    {moment(endOn).format('MMM D')}
                </CustomText>
            </RootStack>
        );
    }
};

const AvatarButtonBase = styled(ButtonBase)(({ hasOwner, isMenuOpen, theme }) => {
    return {
        borderRadius: '50%',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        ...(hasOwner && {
            '&:hover': {
                filter: 'brightness(0.9)',
            },
            '&:active': {
                filter: 'brightness(0.8)',
            },
            ...(isMenuOpen && {
                filter: 'brightness(0.95)',
            }),
        }),
        ...(!hasOwner && {
            border: `1px solid ${theme.palette.ensightful.neutral[700]}`,
            width: 24,
            height: 24,
            background: 'transparent',
            color: theme.palette.ensightful.neutral[700],
            '&:hover': {
                borderColor: theme.palette.ensightful.neutral[900],
                background: theme.palette.ensightful.neutral[200],
                color: theme.palette.ensightful.neutral[900],
            },
            '&:active': {
                background: theme.palette.ensightful.neutral[300],
            },
            ...(isMenuOpen && {
                background: theme.palette.ensightful.neutral[100],
            }),
        }),
    };
});

const CustomAvatarGroup = styled(AvatarGroup)(({ variant, theme }) => {
    return {
        '& > .MuiAvatar-root': {
            border: `2px solid ${
                variant === 'default' ? theme.palette.ensightful.shades[0] : theme.palette.ensightful.primary[50]
            }`,
        },
    };
});

const AvatarIcon = styled(Icon)(() => {
    return {
        color: 'inherit',
    };
});

const CustomPopper = styled(Popper)(({ theme }) => {
    return {
        zIndex: 3,
        borderRadius: '8px',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.shades[0],
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const CustomMenuList = styled(MenuList)(({ theme }) => {
    return {
        borderRadius: '0 0 8px 8px',
        padding: theme.spacing(2),
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const CustomMenuItem = styled(MenuItem)(({ selected, theme }) => {
    return {
        ...(selected && {
            background: theme.palette.ensightful.primary[50],
        }),
    };
});

const CollaboratorsButton = ({
    collaboratorOptions,
    disabled,
    getNameById,
    getUserById,
    task,
    tooltip,
    variant,
    onChangeCollaborator,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [value, setValue] = useState('');

    const open = Boolean(anchorEl);

    const handleClickButton = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClickItem = (e, item) => {
        e.stopPropagation();
        onChangeCollaborator(task.id, item);
    };

    useEffect(() => {
        if (!open) {
            setValue('');
        }
    }, [open]);

    return (
        <>
            <AvatarButtonBase
                aria-label={'assign collaborators'}
                aria-controls={open ? `${task.id}-collaborators-popover` : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup
                disabled={disabled}
                hasOwner={Boolean(task.collaborator_account_ids?.length)}
                isMenuOpen={open}
                onClick={handleClickButton}
            >
                <Tooltip {...getTooltipProps(tooltip)}>
                    {task.collaborator_account_ids?.length ? (
                        <CustomAvatarGroup size={'small'} spacing={'small'} variant={variant}>
                            {task.collaborator_account_ids.map((id, index) => (
                                <Avatar key={id} sx={{ zIndex: index }} size={'small'} user={getUserById(id)} />
                            ))}
                        </CustomAvatarGroup>
                    ) : (
                        <AvatarIcon kind={'avatar'} />
                    )}
                </Tooltip>
            </AvatarButtonBase>
            <ClickAwayListener onClickAway={handleClose}>
                <CustomPopper
                    id={`${task.id}-collaborators-popover`}
                    anchorEl={anchorEl}
                    open={open}
                    placement={'bottom-start'}
                    onClose={handleClose}
                >
                    <TextInput
                        autoFocus
                        label={'Collaborators'}
                        placeholder={'Name or email'}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                    <CustomMenuList>
                        {collaboratorOptions
                            .filter((accountUser) => {
                                return value
                                    ? getNameById(accountUser.owner_account_id)
                                          ?.toLowerCase()
                                          ?.includes(value.toLowerCase()) ||
                                          accountUser?.email?.toLowerCase()?.includes(value.toLowerCase())
                                    : true;
                            })
                            .map((accountUser) => (
                                <CustomMenuItem
                                    key={accountUser.owner_account_id}
                                    selected={Boolean(
                                        task.collaborator_account_ids?.includes(accountUser.owner_account_id)
                                    )}
                                    tabIndex={0}
                                    onClick={(e) => handleClickItem(e, accountUser)}
                                >
                                    <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                        <Checkbox
                                            aria-label={`select ${getNameById(accountUser.owner_account_id)}`}
                                            sx={{ m: -1 }}
                                            checked={Boolean(
                                                task.collaborator_account_ids?.includes(accountUser.owner_account_id)
                                            )}
                                        />
                                        <Avatar size={'small'} user={accountUser} />
                                        {getNameById(accountUser.owner_account_id)}
                                    </Stack>
                                </CustomMenuItem>
                            ))}
                    </CustomMenuList>
                </CustomPopper>
            </ClickAwayListener>
        </>
    );
};

const CustomBox = styled(Box)(({ disabled, isCompleted, isOverdue, showStatus, variant, theme }) => {
    return {
        borderRadius: '8px',
        boxShadow: theme.palette.ensightful.shadows[1],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        ...(variant === 'default' && {
            border: `1px solid ${theme.palette.ensightful.shades[0]}`,
            '&:hover': {
                boxShadow: theme.palette.ensightful.shadows[2],
            },
        }),
        ...(variant === 'milestone' && {
            border: `1px solid ${theme.palette.ensightful.primary[50]}`,
            '&:hover': {
                boxShadow: theme.palette.ensightful.shadows[2],
            },
        }),
        ...(isOverdue && {
            borderColor: theme.palette.ensightful.error[700],
        }),
        ...(isCompleted && {
            borderColor: theme.palette.ensightful.success[700],
        }),
        ...(showStatus
            ? {
                  width: '370px',
              }
            : {
                  maxWidth: '308px',
              }),
        ...(disabled && {
            opacity: 0.25,
        }),
    };
});

const CustomStack = styled(Stack)(({ disabled, isCompleted, isExpanded, variant, theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(4),
        background: theme.palette.ensightful.shades[0],
        ...(isExpanded && {
            borderRadius: '8px 8px 0 0',
        }),
        ...(variant === 'default' && {
            cursor: 'pointer',
            '&:active': {
                background: theme.palette.ensightful.neutral[50],
                ...(isCompleted && {
                    background: theme.palette.ensightful.success[100],
                }),
            },
        }),
        ...(variant === 'milestone' && {
            background: theme.palette.ensightful.primary[50],
            cursor: 'pointer',
            '&:active': {
                background: theme.palette.ensightful.primary[100],
                ...(isCompleted && {
                    background: theme.palette.ensightful.success[100],
                }),
            },
        }),
        ...(isCompleted && {
            background: theme.palette.ensightful.success[50],
        }),
        ...(disabled && {
            cursor: 'default',
        }),
    };
});

const CustomButton = styled(Button)(({ isExpanded, theme }) => {
    return {
        height: '100%',
        padding: theme.spacing(0, 2),
        '& > .MuiButton-startIcon': {
            marginTop: '2px',
            marginRight: '5px',
        },
        '& > .MuiButton-endIcon': {
            marginTop: '2px',
            marginLeft: '3px',
        },
        '&:hover': {
            background: theme.palette.ensightful.neutral[200],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[300],
        },
        ...(isExpanded && {
            background: theme.palette.ensightful.neutral[100],
        }),
    };
});

const SubtaskStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '0 0 8px 8px',
        background: theme.palette.ensightful.shades[0],
        padding: theme.spacing(4),
    };
});

const SubtaskButton = styled(Button)(() => {
    return {
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        '& > .MuiBox-root': {
            minWidth: 0,
            width: '100%',
        },
    };
});

export default forwardRef(
    (
        {
            disabled,
            task,
            variant = 'default',
            showStatus = false,
            getNameById,
            getUserById,
            actionOptions,
            collaboratorOptions,
            onChangeCollaborator,
            onClick = () => {},
            onClickComplete,
            onSelectAction,
            ...rest
        },
        ref
    ) => {
        if (!VARIANTS.includes(variant)) throw new Error(`Shell: Invalid variant "${variant}" in component TaskCard.`);

        const [isExpanded, setIsExpanded] = useState(false);

        const isCompleted = task.status === 'completed';

        const now = new Date().getTime();
        const formattedToday = moment(now).format('MMM D YYYY');
        const formattedDueDate = moment(task.end_on).format('MMM D YYYY');
        const isOverdue = formattedDueDate !== formattedToday && task.end_on < now;

        const spacing = {
            default: 3,
            simplified: 2,
            milestone: 3,
        }[variant];

        return (
            <CustomBox
                ref={ref}
                {...rest}
                disabled={disabled}
                isCompleted={isCompleted}
                isOverdue={isOverdue}
                showStatus={showStatus}
                tabIndex={0}
                variant={variant}
            >
                <CustomStack
                    disabled={disabled}
                    isCompleted={isCompleted}
                    isExpanded={isExpanded}
                    role={'presentation'}
                    variant={variant}
                    onClick={disabled ? () => {} : () => onClick(task.id)}
                >
                    <Stack spacing={spacing}>
                        <Stack direction={'row'} justifyContent={'space-between'}>
                            <Stack sx={{ minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={2}>
                                {variant !== 'simplified' && (
                                    <CompletionButton
                                        aria-label={task.status === 'completed' ? 'uncomplete' : 'complete'}
                                        checked={task.status === 'completed'}
                                        disabled={disabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickComplete(
                                                task.id,
                                                task.status === 'completed' ? 'toDo' : 'completed'
                                            );
                                        }}
                                    />
                                )}
                                <Text noWrap title={task.title} variant={'p1'}>
                                    {task.title}
                                </Text>
                            </Stack>
                            {variant !== 'simplified' && (
                                <Dropdown
                                    id={`${task.id}-dropdown`}
                                    options={actionOptions}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more task options'}
                                            disabled={disabled}
                                            kind={'moreHorizontal'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    onSelect={onSelectAction}
                                />
                            )}
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            {variant === 'milestone' && (
                                <Tag
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts.milestones.title,
                                            tooltipTexts.milestones.description
                                        ),
                                    }}
                                    type={'task'}
                                    value={'milestone'}
                                />
                            )}
                            <DueDateTag
                                isCompleted={isCompleted}
                                isOverdue={isOverdue}
                                endOn={task.end_on}
                                tooltip={{
                                    component: getTooltipComponent(
                                        tooltipTexts.due_date.title,
                                        tooltipTexts.due_date.description
                                    ),
                                }}
                                variant={variant}
                            />
                            {showStatus && !isCompleted && (
                                <Tag
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts.status.select[task.status].title,
                                            tooltipTexts.status.select[task.status].description
                                        ),
                                    }}
                                    type={'status'}
                                    value={task.status}
                                />
                            )}
                            <Tag
                                tooltip={{
                                    component: getTooltipComponent(
                                        tooltipTexts.workload.select[task.workload].title,
                                        tooltipTexts.workload.select[task.workload].description
                                    ),
                                }}
                                type={'workload'}
                                value={task.workload}
                            />
                        </Stack>
                        <Stack sx={{ height: '24px' }} direction={'row'} justifyContent={'space-between'}>
                            {variant === 'default' || variant === 'milestone' ? (
                                <CollaboratorsButton
                                    collaboratorOptions={collaboratorOptions}
                                    disabled={disabled}
                                    getNameById={getNameById}
                                    getUserById={getUserById}
                                    task={task}
                                    tooltip={{ component: tooltipTexts.collaborators.title }}
                                    variant={variant}
                                    onChangeCollaborator={onChangeCollaborator}
                                />
                            ) : (
                                <Box sx={{ width: 24, height: 24 }} />
                            )}
                            {variant !== 'simplified' && task.subtasks.length ? (
                                <CustomButton
                                    color={'neutral'}
                                    disabled={disabled}
                                    endIcon={<Icon kind={isExpanded ? 'dropdownArrowUp' : 'dropdownArrowDown'} />}
                                    isExpanded={isExpanded}
                                    startIcon={<Icon kind={variant === 'milestone' ? 'link' : 'subtask'} />}
                                    tooltip={{
                                        component:
                                            variant === 'milestone'
                                                ? tooltipTexts.tasks_associated[isExpanded].title
                                                : tooltipTexts.subtasks[isExpanded].title,
                                    }}
                                    variant={'borderless'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsExpanded((current) => !current);
                                    }}
                                >
                                    {task.subtasks.length}
                                </CustomButton>
                            ) : null}
                        </Stack>
                    </Stack>
                </CustomStack>
                {isExpanded && task.subtasks.length ? (
                    <SubtaskStack spacing={3}>
                        <Text variant={'p2'}>{variant === 'milestone' ? 'Tasks Associated' : 'Subtasks'}</Text>
                        {task.subtasks.map((subtask) => (
                            <Stack
                                key={subtask.id}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                                spacing={3}
                            >
                                <CompletionButton
                                    aria-label={subtask.status === 'completed' ? 'uncomplete' : 'complete'}
                                    checked={subtask.status === 'completed'}
                                    disabled={disabled}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClickComplete(
                                            subtask.id,
                                            subtask.status === 'completed' ? 'toDo' : 'completed'
                                        );
                                    }}
                                />
                                <SubtaskButton variant={'borderless'} onClick={() => onClick(subtask.id)}>
                                    <Text color={'inherit'} noWrap>
                                        {subtask.title}
                                    </Text>
                                </SubtaskButton>
                                <Stack
                                    sx={{ flexShrink: 0, height: '24px' }}
                                    alignItems={'center'}
                                    direction={'row'}
                                    spacing={2}
                                >
                                    <Text>{moment(subtask.end_on).format('MMM D')}</Text>
                                    <CollaboratorsButton
                                        collaboratorOptions={collaboratorOptions}
                                        disabled={disabled}
                                        getNameById={getNameById}
                                        getUserById={getUserById}
                                        task={subtask}
                                        tooltip={{ component: tooltipTexts.collaborators.title }}
                                        variant={'default'}
                                        onChangeCollaborator={onChangeCollaborator}
                                    />
                                </Stack>
                            </Stack>
                        ))}
                    </SubtaskStack>
                ) : null}
            </CustomBox>
        );
    }
);
